import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentCutIcon from '@mui/icons-material/ContentCut';

import CoachTeamTagTable from './teamTagTable';
import { editCreateCommand, toSecond } from '../../components/utilities';
import GameService from '../../../../services/game.service';
import EditFolderTreeView from '../treeview';

import Alert from '../../../../common/alert';

const EditTagTable = ({ loading, tagList, setIdx, selected, sort, name, update, showPlay, t }) => {
    const [teamTagList, setTeamTagList] = useState([]);
    const [checkArray, setCheckArray] = useState([]);
    const [eventName, setEventName] = useState('');
    const [controlEdit, setControlEdit] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [showAlert, setShowAlert] = useState(false);
    const [showResultAlert, setShowResultAlert] = useState(false);
    const [downState, setDownState] = useState('');

    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    useEffect(() => {
        if (showResultAlert) {
            const timer = setTimeout(() => {
                setShowResultAlert(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showResultAlert]);

    const handleRender = async () => {
        setShowAlert(true);
        // window.alert('Render alert');
        let newList = [];
        const temp = checkArray.length === 0 ? teamTagList : teamTagList.filter((item) => checkArray.includes(item.id) === true);
        temp.map((tag) => {
            let last = newList.at(-1);

            if (last && toSecond(last?.end_time ?? 0) >= toSecond(tag.start_time) && toSecond(last?.start_time ?? 0) <= toSecond(tag.start_time)) {
                last.end_time = last.end_time > tag.end_time ? last.end_time : tag.end_time;
            } else {
                newList.push({ ...tag });
            }
        });
        const result = await editCreateCommand(newList, name);
        setShowResultAlert(true);
        setDownState(result);
    };

    const handleDelete = async () => {
        if (checkArray.length === 0) {
            window.alert('Please select clips to delete');

            return;
        }

        const deleteList = teamTagList.filter((item) => checkArray.includes(item.id) === true).map((data) => data.id);

        try {
            await GameService.deleteEditClip(deleteList.join(','));
            window.alert('Clips deleted successfully!');
            setCheckArray([]);
            update();
        } catch (error) {
            window.alert('Failed to delete clips. Please try again.');
            console.error('Delete error:', error);
        }
    };

    const handleCopy = async () => {
        setEventName('copy');

        if (checkArray.length === 0) {
            window.alert('Please select clips to copy to other edit');

            return;
        }

        if (controlEdit === null) {
            setDialogOpen(true);

            return;
        }

        if (controlEdit.type !== 'edit') {
            window.alert('You selected folder. Please select edit correctly.');
            setDialogOpen(true);

            return;
        }

        let newList = teamTagList.filter((item) => checkArray.includes(item.id) === true).map((item) => item.id);

        try {
            await GameService.copyEditClips(newList.join(','), controlEdit.id);
            window.alert('Clips copied successfully!');
            setCheckArray([]);
            setControlEdit(null);
            setEventName('');
        } catch (error) {
            window.alert('Failed to copy clips. Please try again.');
            console.error('Copy error:', error);
        }
    };

    const handleMove = async () => {
        setEventName('move');

        if (checkArray.length === 0) {
            window.alert('Please select clips to move to other edit');

            return;
        }

        if (controlEdit === null) {
            setDialogOpen(true);

            return;
        }

        if (controlEdit.type !== 'edit') {
            window.alert('You selected folder. Please select edit correctly.');
            setDialogOpen(true);

            return;
        }

        const moveList = teamTagList.filter((item) => checkArray.includes(item.id) === true).map((item) => item.id);

        try {
            await GameService.moveEditClips(moveList.join(','), controlEdit.id);
            window.alert('Clips moved successfully!');
            setCheckArray([]);
            setControlEdit(null);
            setEventName('');
            update();
        } catch (error) {
            window.alert('Failed to move clips. Please try again.');
            console.error('Move error:', error);
        }
    };

    const handleSave = () => {
        if (controlEdit === null) {
            window.alert("You didn't select any edit. Please select.");

            return;
        }

        setDialogOpen(false);
        eventName === 'copy' ? handleCopy() : handleMove();
    };

    const handleClose = () => {
        setControlEdit(null);
        setDialogOpen(false);
    };

    useEffect(() => {
        setTeamTagList(tagList);
    }, [tagList]);

    return (
        <Box sx={{ width: '480px', height: '100%', padding: '16px 5px', borderLeft: '1px solid #E8E8E8', textAlign: 'center' }}>
            {loading && (
                <div style={{ width: '100%', height: '100%', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    {showAlert && <Alert text={'Render started!'} />}
                    {showResultAlert && <Alert text={downState} />}

                    {teamTagList.length === 0 && (
                        <Box sx={{ width: '100%', height: '5vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '16px', fontWeight: 600, color: '#1a1b1d' }}>{t('No Tags')}</Typography>
                        </Box>
                    )}
                    {teamTagList.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 10px 24px' }}>
                            <Button
                                variant="contained"
                                sx={{ display: 'none', fontSize: '0.75rem !important', background: '#5C738E', color: '#ffffff', '&:hover': { background: '#17375E', color: '#fff' } }}
                                onClick={handleRender}
                            >
                                {t('Render')}
                            </Button>
                            <Button
                                variant="contained"
                                disabled={checkArray.length === 0}
                                sx={{ fontSize: '0.75rem !important', background: '#5C738E', color: '#ffffff', '&:hover': { background: '#17375E', color: '#fff' } }}
                                onClick={handleMove}
                            >
                                <ContentCutIcon sx={{ height: '15px' }} />
                                {t('Move')}
                            </Button>
                            <Button
                                variant="contained"
                                disabled={checkArray.length === 0}
                                sx={{ fontSize: '0.75rem !important', background: '#5C738E', color: '#ffffff', '&:hover': { background: '#17375E', color: '#fff' } }}
                                onClick={handleCopy}
                            >
                                <ContentCopyIcon sx={{ height: '15px' }} />
                                {t('Copy')}
                            </Button>
                            <Button
                                variant="contained"
                                disabled={checkArray.length === 0}
                                sx={{ fontSize: '0.75rem !important', background: '#5C738E', color: '#ffffff', '&:hover': { background: '#17375E', color: '#fff' } }}
                                onClick={handleDelete}
                            >
                                <DeleteIcon sx={{ height: '15px' }} />
                                {t('Delete')}
                            </Button>
                        </Box>
                    )}
                    {teamTagList.length > 0 && (
                        <CoachTeamTagTable t={t} tagList={teamTagList} setIndex={setIdx} selectIdx={selected} handleSort={sort} updateTable={update} setChecks={setCheckArray} showPlay={showPlay} />
                    )}
                    <Dialog open={dialogOpen} onClose={() => handleClose()} scroll="paper">
                        <DialogTitle>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '16px', fontWeight: 600, color: '#1a1b1d' }}>{t('Select Edit')}</Typography>
                        </DialogTitle>
                        <DialogContent dividers={true} style={{ display: 'flex' }}>
                            <EditFolderTreeView t={t} setEdit={setControlEdit} isMain={false} entireHeight="400px" treeHeight="100%" />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => handleClose()}>{t('Cancel')}</Button>
                            <Button variant="outlined" onClick={() => handleSave()}>
                                {eventName === 'copy' ? 'Copy' : 'Move'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Box>
    );
};

export default EditTagTable;
